import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid, alpha, Container, Chip } from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  Description as DocIcon,
  Inventory as PalletIcon,
  LocalShipping as VehicleIcon,
  Warning as ExceptionIcon,
  CloudUpload as UploadIcon,
  AutoAwesome as AiIcon,
  CheckCircle as CheckIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingIcon,
  DataObject as DataIcon,
  Analytics as AnalyticsIcon,
  IntegrationInstructions as IntegrationIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import { useLanguage } from '../i18n';
import Navbar from './Navbar';
import LandingFooter from './LandingFooter';
import Lightfall from './Lightfall';


/* ─── Feature Card (compact) ──────────────────────── */
const FeatureCard: React.FC<{
  icon: React.ReactNode; title: string; desc: string; gradient: string; highlights: string[];
}> = ({ icon, title, desc, gradient, highlights }) => (
  <Box sx={{
    p: 3, borderRadius: 2, background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)', transition: 'all 0.3s ease',
    height: '100%', display: 'flex', flexDirection: 'column',
    '&:hover': { transform: 'translateY(-4px)', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(139,92,246,0.25)', boxShadow: '0 12px 40px rgba(139,92,246,0.1)' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 1.5, background: gradient,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        boxShadow: `0 4px 16px ${alpha('#8B5CF6', 0.25)}`,
      }}>{icon}</Box>
      <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{title}</Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, mb: 1.5, fontSize: '0.82rem' }}>{desc}</Typography>
    <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {highlights.map((h, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CheckIcon sx={{ fontSize: 12, color: '#34D399' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem' }}>{h}</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

/* ─── Connector Card ──────────────────────────────── */
const CONNECTORS = [
  { name: 'Salesforce', color: '#00A1E0' },
  { name: 'SAP / ERP', color: '#F0AB00' },
  { name: 'AWS S3', color: '#FF9900' },
  { name: 'Azure', color: '#0089D6' },
  { name: 'GCP', color: '#4285F4' },
  { name: 'Oracle', color: '#F80000' },
  { name: 'SharePoint', color: '#0078D4' },
  { name: 'Snowflake', color: '#29B5E8' },
];

const ConnectorCard: React.FC<{ title: string; desc: string }> = ({ title, desc }) => (
  <Box sx={{
    p: 3, borderRadius: 2, background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)', transition: 'all 0.3s ease',
    height: '100%', display: 'flex', flexDirection: 'column',
    '&:hover': { transform: 'translateY(-4px)', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(59,130,246,0.3)', boxShadow: '0 12px 40px rgba(59,130,246,0.12)' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 1.5,
        background: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        boxShadow: '0 4px 16px rgba(14,165,233,0.3)',
      }}>
        <CloudIcon sx={{ color: '#fff', fontSize: 22 }} />
      </Box>
      <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{title}</Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, mb: 2, fontSize: '0.82rem' }}>{desc}</Typography>
    <Box sx={{ mt: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {CONNECTORS.map((c) => (
        <Box key={c.name} sx={{
          px: 1.25, py: 0.35, borderRadius: 1,
          background: `${c.color}18`,
          border: `1px solid ${c.color}40`,
          display: 'flex', alignItems: 'center', gap: 0.5,
        }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.name}</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

/* ─── Step ─────────────────────────────────────────── */
const StepCard: React.FC<{ step: number; icon: React.ReactNode; title: string; desc: string }> = ({ step, icon, title, desc }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Box sx={{
      width: 48, height: 48, borderRadius: 1.5, flexShrink: 0,
      background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(59,130,246,0.1) 100%)',
      border: '1px solid rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
    }}>
      {icon}
      <Box sx={{
        position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%',
        background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 800 }}>{step}</Typography>
      </Box>
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, fontSize: '0.82rem' }}>{desc}</Typography>
    </Box>
  </Box>
);

/* ─── Stat ─────────────────────────────────────────── */
const StatItem: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography sx={{
      fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800,
      background: 'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)',
      backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.1,
    }}>{value}</Typography>
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontSize: '0.68rem', letterSpacing: '0.04em' }}>{label}</Typography>
  </Box>
);

/* ─── Benefit Row ──────────────────────────────────── */
const BenefitRow: React.FC<{ icon: React.ReactNode; title: string; desc: string }> = ({ icon, title, desc }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Box sx={{ color: '#A78BFA', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem', mb: 0.25 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', lineHeight: 1.5 }}>{desc}</Typography>
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════════ */
const LandingPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(165deg, #0A0E27 0%, #111633 40%, #1A1F3A 100%)', overflow: 'hidden' }}>
      <Navbar />

      {/* ══════ HERO ══════════════════════════════════════════ */}
      <Box id="hero" sx={{ position: 'relative', display: 'flex', alignItems: 'center', minHeight: '100vh' }}>
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <Lightfall colors={['#A78BFA', '#6D28D9', '#3B82F6']} backgroundColor="#0A0E27" dpr={0.5} speed={0.2} streakCount={6} streakWidth={0.5} streakLength={0.8} glow={0.8} density={0.6} twinkle={0.4} zoom={2.5} backgroundGlow={0.5} opacity={0.7} mouseInteraction={false} />
        </Box>
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, background: 'linear-gradient(180deg, rgba(10,14,39,0.2) 0%, rgba(10,14,39,0.5) 60%, rgba(10,14,39,0.9) 100%)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            {/* Left: Text */}
            <Grid item xs={12} md={7}>
              <Box sx={{ animation: 'fadeInUp 0.6s ease-out' }}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, borderRadius: 5,
                  background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)', mb: 2.5,
                }}>
                  <AiIcon sx={{ fontSize: 14, color: '#A78BFA' }} />
                  <Typography variant="caption" sx={{ color: '#A78BFA', fontWeight: 600, letterSpacing: '0.04em', fontSize: '0.7rem' }}>{t.landing.hero.badge}</Typography>
                </Box>

                <Typography variant="h2" sx={{
                  fontWeight: 800, fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
                  lineHeight: 1.1, mb: 2, color: '#fff',
                  '& span': { background: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 40%, #3B82F6 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
                }}>
                  {t.landing.hero.title.split('|')[0]}
                  <br /><span>{t.landing.hero.title.split('|')[1]}</span>
                </Typography>

                <Typography sx={{ color: 'rgba(255,255,255,0.9)', maxWidth: 520, mb: 3, lineHeight: 1.7, fontSize: '0.95rem' }}>
                  {t.landing.hero.subtitle}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                  <Button size="large" endIcon={<ArrowIcon />} onClick={() => navigate('/login')} sx={{
                    px: 3.5, py: 1.5, fontSize: '0.9rem', fontWeight: 700, color: '#fff',
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                    boxShadow: '0 6px 24px rgba(139,92,246,0.35)', borderRadius: 1.5,
                    '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)', transform: 'translateY(-1px)' },
                    transition: 'all 0.3s ease',
                  }}>{t.landing.hero.cta}</Button>
                  <Button size="large" onClick={() => { const el = document.getElementById('features'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} sx={{
                    px: 3.5, py: 1.5, fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)', borderRadius: 1.5,
                    '&:hover': { border: '1px solid rgba(139,92,246,0.4)', background: 'rgba(139,92,246,0.06)' },
                  }}>{t.landing.hero.ctaSecondary}</Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {[
                    { icon: <SecurityIcon sx={{ fontSize: 14 }} />, text: t.landing.hero.highlights.security },
                    { icon: <SpeedIcon sx={{ fontSize: 14 }} />, text: t.landing.hero.highlights.processing },
                    { icon: <TrendingIcon sx={{ fontSize: 14 }} />, text: t.landing.hero.highlights.accuracy },
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ color: '#A78BFA' }}>{item.icon}</Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontSize: '0.68rem' }}>{item.text}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Right: Stats + mini highlights */}
            <Grid item xs={12} md={5}>
              <Box sx={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}>
                <Box sx={{
                  display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2,
                  p: 3, borderRadius: 2, background: 'rgba(10, 14, 39, 0.65)', border: '1px solid rgba(255, 255, 255, 0.23)', backdropFilter: 'blur(12px)',
                }}>
                  <StatItem value={t.landing.stats.accuracy} label={t.landing.stats.accuracyLabel} />
                  <StatItem value={t.landing.stats.speed} label={t.landing.stats.speedLabel} />
                  <StatItem value={t.landing.stats.processes} label={t.landing.stats.processesLabel} />
                  <StatItem value={t.landing.stats.documents} label={t.landing.stats.documentsLabel} />
                </Box>
                {/* Mini process list */}
                <Box sx={{ mt: 2, p: 2.5, borderRadius: 2, background: 'rgba(10, 14, 39, 0.65)', border: '1px solid rgba(255, 255, 255, 0.23)', backdropFilter: 'blur(12px)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.6rem', mb: 1.5, display: 'block' }}>
                    {t.landing.automatedProcesses.label}
                  </Typography>
                  {[
                    { label: t.landing.automatedProcesses.structured, color: '#8B5CF6' },
                    { label: t.landing.automatedProcesses.semiStructured, color: '#3B82F6' },
                    { label: t.landing.automatedProcesses.unstructured, color: '#10B981' },
                    { label: t.landing.automatedProcesses.multimedia, color: '#F59E0B' },
                  ].map((p, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.72rem' }}>{p.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════ TECH STRIP ═════════════════════════════════════ */}
      <Box sx={{ position: 'relative', zIndex: 1, py: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', py: 1.5,
            borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.6rem' }}>{t.landing.techStrip.poweredBy}</Typography>
            {['Google Cloud', 'Document AI', 'Vertex AI', 'Gemini 2.5', 'Cloud Storage'].map((tech) => (
              <Typography key={tech} variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', fontWeight: 600, fontSize: '0.75rem' }}>{tech}</Typography>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ══════ FEATURES ═══════════════════════════════════════ */}
      <Box id="features" sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 3.5 }}>
            <Chip label={t.landing.features.sectionTitle} size="small" sx={{
              mb: 1.5, background: 'rgba(139,92,246,0.1)', color: '#A78BFA', fontWeight: 600,
              border: '1px solid rgba(139,92,246,0.2)', fontSize: '0.65rem', letterSpacing: '0.06em', height: 26,
            }} />
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.5rem', md: '1.85rem' }, mb: 1 }}>
              {t.landing.features.sectionTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', maxWidth: 450, mx: 'auto', fontSize: '0.82rem' }}>
              {t.landing.features.sectionSubtitle}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={"auto" as any} sx={{ flexGrow: 1, flexBasis: { lg: '18%' } }}>
              <FeatureCard icon={<DocIcon sx={{ color: '#fff', fontSize: 22 }} />}
                title={t.landing.features.docAi.title} desc={t.landing.features.docAi.desc}
                gradient="linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
                highlights={t.landing.features.docAi.highlights}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={"auto" as any} sx={{ flexGrow: 1, flexBasis: { lg: '18%' } }}>
              <FeatureCard icon={<PalletIcon sx={{ color: '#fff', fontSize: 22 }} />}
                title={t.landing.features.pallet.title} desc={t.landing.features.pallet.desc}
                gradient="linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)"
                highlights={t.landing.features.pallet.highlights}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={"auto" as any} sx={{ flexGrow: 1, flexBasis: { lg: '18%' } }}>
              <FeatureCard icon={<VehicleIcon sx={{ color: '#fff', fontSize: 22 }} />}
                title={t.landing.features.vehicle.title} desc={t.landing.features.vehicle.desc}
                gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
                highlights={t.landing.features.vehicle.highlights}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={"auto" as any} sx={{ flexGrow: 1, flexBasis: { lg: '18%' } }}>
              <FeatureCard icon={<ExceptionIcon sx={{ color: '#fff', fontSize: 22 }} />}
                title={t.landing.features.exceptions.title} desc={t.landing.features.exceptions.desc}
                gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                highlights={t.landing.features.exceptions.highlights}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={"auto" as any} sx={{ flexGrow: 1, flexBasis: { lg: '18%' } }}>
              <ConnectorCard
                title={t.landing.features.connectors?.title || 'Connect Your Data Source'}
                desc={t.landing.features.connectors?.desc || 'Plug into your existing enterprise systems. Native connectors for cloud platforms, CRMs, ERPs, and data warehouses — no custom integration required.'}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════ WHY CHOOSE US (2-col detail) ══════════════════ */}
      <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Chip label={t.landing.whyChooseUs.chip} size="small" sx={{
                mb: 1.5, background: 'rgba(139,92,246,0.1)', color: '#A78BFA', fontWeight: 600,
                border: '1px solid rgba(139,92,246,0.2)', fontSize: '0.65rem', height: 26,
              }} />
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.5rem', md: '1.85rem' }, mb: 1 }}>
                {t.landing.whyChooseUs.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, mb: 2, fontSize: '0.85rem' }}>
                {t.landing.whyChooseUs.subtitle}
              </Typography>
              <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 16 }} />} onClick={() => navigate('/login')} sx={{
                color: '#A78BFA', fontWeight: 600, fontSize: '0.8rem', px: 0,
                '&:hover': { background: 'transparent', color: '#fff' },
              }}>
                {t.landing.whyChooseUs.cta}
              </Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                <BenefitRow icon={<DataIcon sx={{ fontSize: 20 }} />} title={t.landing.whyChooseUs.benefit1Title}
                  desc={t.landing.whyChooseUs.benefit1Desc} />
                <BenefitRow icon={<AnalyticsIcon sx={{ fontSize: 20 }} />} title={t.landing.whyChooseUs.benefit2Title}
                  desc={t.landing.whyChooseUs.benefit2Desc} />
                <BenefitRow icon={<IntegrationIcon sx={{ fontSize: 20 }} />} title={t.landing.whyChooseUs.benefit3Title}
                  desc={t.landing.whyChooseUs.benefit3Desc} />
                <BenefitRow icon={<SecurityIcon sx={{ fontSize: 20 }} />} title={t.landing.whyChooseUs.benefit4Title}
                  desc={t.landing.whyChooseUs.benefit4Desc} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════ HOW IT WORKS ═══════════════════════════════════ */}
      <Box id="how-it-works" sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Left: steps */}
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <StepCard step={1} icon={<UploadIcon sx={{ color: '#A78BFA', fontSize: 22 }} />}
                  title={t.landing.howItWorks.step1.title} desc={t.landing.howItWorks.step1.desc} />
                <StepCard step={2} icon={<AiIcon sx={{ color: '#A78BFA', fontSize: 22 }} />}
                  title={t.landing.howItWorks.step2.title} desc={t.landing.howItWorks.step2.desc} />
                <StepCard step={3} icon={<CheckIcon sx={{ color: '#A78BFA', fontSize: 22 }} />}
                  title={t.landing.howItWorks.step3.title} desc={t.landing.howItWorks.step3.desc} />
              </Box>
            </Grid>
            {/* Right: section header + CTA */}
            <Grid item xs={12} md={5} sx={{ order: { xs: -1, md: 0 } }}>
              <Chip label={t.landing.howItWorks.sectionTitle} size="small" sx={{
                mb: 1.5, background: 'rgba(139,92,246,0.1)', color: '#A78BFA', fontWeight: 600,
                border: '1px solid rgba(139,92,246,0.2)', fontSize: '0.65rem', height: 26,
              }} />
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.5rem', md: '1.85rem' }, mb: 1 }}>
                {t.landing.howItWorks.sectionTitle}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, mb: 2.5, fontSize: '0.85rem' }}>
                {t.landing.howItWorks.sectionSubtitle}
              </Typography>
              <Box sx={{ p: 2.5, borderRadius: 2, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                  {t.landing.howItWorksAccuracy.label}
                </Typography>
                <Typography sx={{ color: '#A78BFA', fontWeight: 800, fontSize: '1.5rem', lineHeight: 1 }}>
                  {t.landing.stats.accuracy}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}>
                  {t.landing.howItWorksAccuracy.poweredBy}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════ CTA ════════════════════════════════════════════ */}
      <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 5 } }}>
        <Container maxWidth="md">
          <Box sx={{
            textAlign: 'center', p: { xs: 3.5, md: 5 }, borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(59,130,246,0.06) 100%)',
            border: '1px solid rgba(139,92,246,0.15)', position: 'relative', overflow: 'hidden',
          }}>
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)' }} />
            <Typography sx={{ color: '#fff', fontWeight: 800, mb: 1, position: 'relative', fontSize: { xs: '1.35rem', md: '1.75rem' } }}>
              {t.landing.cta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 3, maxWidth: 400, mx: 'auto', position: 'relative', fontSize: '0.85rem' }}>
              {t.landing.cta.subtitle}
            </Typography>
            <Button size="large" endIcon={<ArrowIcon />} onClick={() => navigate('/login')} sx={{
              px: 4, py: 1.5, fontSize: '0.9rem', fontWeight: 700, color: '#fff',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
              boxShadow: '0 6px 24px rgba(139,92,246,0.35)', borderRadius: 1.5, position: 'relative',
              '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)', transform: 'translateY(-1px)' },
              transition: 'all 0.3s ease',
            }}>{t.landing.cta.button}</Button>
          </Box>
        </Container>
      </Box>

      <LandingFooter />
    </Box>
  );
};

export default LandingPage;
